/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 *
 * The Breakdown's drill-down (PLAT-1819): ONE table, two levels. Each main
 * row is a pr-review RUN with its consolidated metrics (wall-clock, cost,
 * turns, tokens, models) and a hard-data summary of the findings it reported
 * (count, severity mix, status mix, files touched); expanding a run reveals
 * the granular finding entries in the same grid. Every cell is exact or a
 * stated absence. Plus the compact quality summary strip shown above the
 * Breakdown table.
 */
import React, { useState } from 'react';
import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
  PASS_LABELS,
  PrRun,
  ReviewFindingRow,
  SEVERITY_LABELS,
  STATE_LABELS,
  formatDurationMs,
  formatFixRate,
  summarizeReviewQuality,
} from './claudeReviewQuality';
import { formatTokens, formatUsd, githubUrl, tzShortName } from './claudeMetricsView';
import { RunQueryFilters, useGroupRuns } from './usePrRuns';
import { PeriodScope } from './claudeReviewQualityJoin';

const useStyles = makeStyles(theme => ({
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing(2.5),
    padding: theme.spacing(1, 0, 1.5),
  },
  summaryFigure: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
  },
  numeric: {
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  drill: {
    padding: theme.spacing(1, 0, 1.5),
    // One authored moment: the drill settles in instead of popping. Honors
    // reduced motion (a11y commitment in PRODUCT.md).
    animation: '$drillIn 160ms ease-out',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  '@keyframes drillIn': {
    from: { opacity: 0, transform: 'translateY(-2px)' },
    to: { opacity: 1, transform: 'none' },
  },
  // Room for the one column that SHOULD wrap, at a readable measure; width
  // flows into the outer table's single horizontal scroll.
  drillTable: {
    minWidth: 1460,
  },
  findingCell: {
    minWidth: 170,
  },
  // A finding entry is ONE full-width line under its run: fixed-width lead
  // segments (severity, status, file) act as pseudo-columns that align across
  // subrows; the title takes the remaining measure.
  findingSubrow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(7),
    whiteSpace: 'nowrap',
  },
  // The expanded block is CONTAINED: a shared tint plus hairlines between
  // entries, so the entries read as the run's contents rather than loose rows.
  // `&&` doubles the class — the outer tables' `td` rules (border: none,
  // padding) are more specific than a single class and would silently win.
  subCell: {
    '&&': {
      backgroundColor: theme.palette.action.hover,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  // The one-per-run label line naming the entry segments. Same width classes
  // as the entries, so the labels ARE the column guides.
  subLabels: {
    '& > span': {
      color: theme.palette.text.secondary,
      fontSize: '0.66rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
    },
  },
  subSeverity: { width: 90, flexShrink: 0 },
  subStatus: { width: 90, flexShrink: 0 },
  subFile: {
    width: 260,
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subTitle: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subRounds: {
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
    color: theme.palette.text.secondary,
  },
  drillNote: {
    marginTop: theme.spacing(0.75),
  },
  quietText: {
    color: theme.palette.text.secondary,
  },
  // Constant-width slot for the expander, rendered on EVERY run row: rows
  // without findings carry an empty slot, so PR links align down the column
  // instead of jumping 22px between expandable and plain rows.
  expandSlot: {
    display: 'inline-block',
    width: '1.1rem',
    marginRight: theme.spacing(0.5),
    verticalAlign: 'middle',
  },
  expandBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    lineHeight: 0,
    color: 'inherit',
    '& svg': { fontSize: '1.1rem' },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },
  sortBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },
  sortArrow: {
    marginLeft: theme.spacing(0.5),
    fontSize: '0.7em',
  },
  expandAll: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },
  drillHead: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(2),
    // Line the caption up with the table's first column text (the cells'
    // 16px lead padding) so it belongs to the table instead of hanging left.
    padding: theme.spacing(0, 2),
    marginBottom: theme.spacing(0.5),
  },
  // The drill's caption speaks in the table's own voice — a quiet small-caps
  // label at column-head weight, not a stray sentence.
  drillTitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.72rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
}));

/** One run-level group of the drill: a run and the findings it reported. */
type RunGroup = {
  key: string;
  run?: PrRun;
  /** Set when the reporting run is outside the fetched union. */
  orphanRunId?: string;
  orphanSlug?: { org: string; repo: string };
  findings: ReviewFindingRow[];
};

/** Bounded display of run rows; the cap is stated, never silent. */
const RUN_ROW_CAP = 40;

const SEVERITY_ORDER = ['blocking', 'major', 'minor', 'suggestion'] as const;
const SEVERITY_RANK: Record<string, number> = {
  blocking: 0,
  major: 1,
  minor: 2,
  suggestion: 3,
};
const STATE_ORDER = ['open', 'fixed', 'closed', 'declined'] as const;

/** Tracked (non-advisory) findings of a group. */
const tracked = (g: RunGroup): ReviewFindingRow[] => g.findings.filter(f => !f.advisory);

/** "1 blocking · 2 major" — the run's severity mix over tracked findings. */
function severityMix(g: RunGroup): string {
  const counts = new Map<string, number>();
  for (const f of tracked(g)) {
    const k = f.severity ?? 'unclassified';
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return [...SEVERITY_ORDER, 'unclassified']
    .filter(k => counts.has(k))
    .map(k => `${counts.get(k)} ${k}`)
    .join(' · ');
}

/** "4 fixed · 1 declined" — the run's status mix over tracked findings. */
function statusMix(g: RunGroup): string {
  const counts = new Map<string, number>();
  for (const f of tracked(g)) {
    counts.set(f.state, (counts.get(f.state) ?? 0) + 1);
  }
  return STATE_ORDER.filter(k => counts.has(k))
    .map(k => `${counts.get(k)} ${k}`)
    .join(' · ');
}

const distinctFiles = (g: RunGroup): number =>
  new Set(g.findings.filter(f => f.file).map(f => f.file)).size;

const worstSeverity = (g: RunGroup): number | undefined => {
  const ranks = tracked(g).map(f => SEVERITY_RANK[f.severity ?? ''] ?? 4);
  return ranks.length ? Math.min(...ranks) : undefined;
};

const maxRound = (g: RunGroup): number | undefined => {
  const rounds = g.findings
    .map(f => f.statusRound ?? f.firstRound)
    .filter((r): r is number => r !== undefined);
  return rounds.length ? Math.max(...rounds) : undefined;
};

/**
 * "2026-08-31, 16:07:35 UTC" — the run's instant in the panel's selected zone,
 * suffixed with the zone's name AT THAT INSTANT (a DST boundary must not
 * relabel winter times with the summer abbreviation). An unparseable timestamp
 * returns undefined — the caller states the absence, never "Invalid Date".
 */
function formatReceivedAt(receivedAt: string, tz: string): string | undefined {
  const instant = new Date(receivedAt);
  if (Number.isNaN(instant.getTime())) return undefined;
  const stamp = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(instant);
  return `${stamp} ${tzShortName(tz, instant)}`;
}

type DrillSortKey =
  | 'pr'
  | 'run'
  | 'when'
  | 'wallClock'
  | 'cost'
  | 'turns'
  | 'tokens'
  | 'files'
  | 'findings'
  | 'severity'
  | 'status'
  | 'rounds';

/**
 * Run-level extractors. `undefined` (an orphan group's run cells, a clean
 * run's finding aggregates) sorts LAST in either direction — absence is not
 * a small value.
 */
const DRILL_SORT: Record<DrillSortKey, (g: RunGroup) => number | string | undefined> = {
  pr: g => {
    const slug = g.run
      ? `${g.run.org}/${g.run.repo}`
      : g.orphanSlug
      ? `${g.orphanSlug.org}/${g.orphanSlug.repo}`
      : undefined;
    const pr = g.run?.pr ?? g.findings[0]?.pr;
    return slug === undefined ? undefined : `${slug}#${pr ?? ''}`;
  },
  run: g => g.run?.runId ?? g.orphanRunId,
  when: g => g.run?.receivedAt,
  wallClock: g => g.run?.durationMs,
  cost: g => g.run?.costUsd,
  turns: g => (g.run ? g.run.models.reduce((n, m) => n + m.msgs, 0) : undefined),
  tokens: g =>
    g.run
      ? g.run.models.reduce(
          (n, m) =>
            n + m.inputTokens + m.outputTokens + m.cacheWriteTokens + m.cacheReadTokens,
          0,
        )
      : undefined,
  files: g => (g.findings.length ? distinctFiles(g) : undefined),
  findings: g => (g.findings.length ? tracked(g).length : undefined),
  severity: g => worstSeverity(g),
  // Open findings first: the run leaving the most unresolved work sorts up.
  status: g =>
    g.findings.length ? -tracked(g).filter(f => f.state === 'open').length : undefined,
  rounds: g => maxRound(g),
};

/** Findings inside an expanded run: severity first, then file — a fixed,
 *  predictable order (the run level owns interactive sorting). */
function orderFindings(findings: ReviewFindingRow[]): ReviewFindingRow[] {
  return [...findings].sort((a, b) => {
    const sev =
      (SEVERITY_RANK[a.severity ?? ''] ?? 4) - (SEVERITY_RANK[b.severity ?? ''] ?? 4);
    if (sev) return sev;
    return (a.file ?? '').localeCompare(b.file ?? '');
  });
}

const dash = (classes: ReturnType<typeof useStyles>, title: string) => (
  <Tooltip title={title}>
    <span tabIndex={0} className={classes.quietText}>
      —
    </span>
  </Tooltip>
);

/**
 * The two-level drill table: one main row per pr-review run in the group
 * (current filters, displayed period) carrying its consolidated metrics and
 * a hard-data summary of the findings it reported; expanding a run reveals
 * the granular finding entries in the same grid. The finding-to-run join is
 * run-id equality — the ledger records the run that last wrote each finding —
 * so a clean run shows a truthful absence and a finding whose reporting run
 * is outside the union appears under its run id with the run cells honestly
 * absent (retention or period). Wall-clock/cost absences mean the emitter
 * predates those fields — never zero.
 */
export const RunFindingsTable = ({
  filters,
  scope,
  tz,
  teamFilter,
  teamOf,
  findings,
}: {
  filters: RunQueryFilters;
  scope: PeriodScope;
  /** The panel's selected timezone — drill timestamps honor the same contract
   *  as every other figure (never unlabeled browser-local time). */
  tz: string;
  /** A team constraint is a client-side projection; filtered here, stated below. */
  teamFilter?: string;
  teamOf?: (org: string, repo: string) => string;
  /** The group's finding evidence, pre-scoped by the same constraint chain. */
  findings: readonly ReviewFindingRow[];
}) => {
  const classes = useStyles();
  const { data } = useGroupRuns({ filters, scope });
  // Latest review activity first by default; every header toggles its column.
  const [sort, setSort] = useState<{ key: DrillSortKey; dir: 'asc' | 'desc' }>({
    key: 'when',
    dir: 'desc',
  });
  // Individually toggled rows; `allOpen` flips the default and `open` holds
  // the exceptions, so "expand all" stays consistent as rows come and go.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [allOpen, setAllOpen] = useState(false);
  if (!data) return null;

  const toggleSort = (key: DrillSortKey) =>
    setSort(current =>
      current.key === key
        ? { key, dir: current.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' },
    );

  const toggleOpen = (key: string) =>
    setOpen(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const SortHead = ({
    label,
    k,
    numeric,
    className,
    title,
  }: {
    label: string;
    k: DrillSortKey;
    numeric?: boolean;
    className?: string;
    title?: string;
  }) => {
    const active = sort.key === k;
    const btn = (
      <button
        type="button"
        className={classes.sortBtn}
        onClick={() => toggleSort(k)}
        aria-label={`Sort by ${label}${
          active && sort.dir === 'desc' ? ', ascending next' : ', descending next'
        }`}
      >
        {label}
        {active && (
          <span aria-hidden className={classes.sortArrow}>
            {sort.dir === 'desc' ? '▼' : '▲'}
          </span>
        )}
      </button>
    );
    return (
      <TableCell
        align={numeric ? 'right' : undefined}
        className={className}
        aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : undefined}
      >
        {title ? (
          <Tooltip title={title}>
            <span tabIndex={0}>{btn}</span>
          </Tooltip>
        ) : (
          btn
        )}
      </TableCell>
    );
  };

  const runs =
    teamFilter !== undefined && teamOf
      ? data.runs.filter(r => teamOf(r.org, r.repo) === teamFilter)
      : data.runs;

  const byRunId = new Map(runs.map(r => [r.runId, r]));
  const groups: RunGroup[] = runs.map(run => ({
    key: run.runId,
    run,
    findings: orderFindings(findings.filter(f => f.runId === run.runId)),
  }));
  // Findings whose reporting run is outside the union: real data, absent run.
  const orphans = new Map<string, ReviewFindingRow[]>();
  for (const f of findings) {
    if (byRunId.has(f.runId)) continue;
    orphans.set(f.runId, [...(orphans.get(f.runId) ?? []), f]);
  }
  for (const [runId, list] of orphans) {
    groups.push({
      key: `orphan:${runId}`,
      orphanRunId: runId,
      orphanSlug: { org: list[0].org, repo: list[0].repo },
      findings: orderFindings(list),
    });
  }

  if (groups.length === 0) {
    return (
      <Typography variant="caption" color="textSecondary" component="div">
        No pr-review runs retained for this group in this period — the run grain
        keeps {data.retentionWeeks} weeks.
      </Typography>
    );
  }

  const extract = DRILL_SORT[sort.key];
  const sorted = [...groups].sort((a, b) => {
    const va = extract(a);
    const vb = extract(b);
    if (va === undefined && vb === undefined) return a.key.localeCompare(b.key);
    if (va === undefined) return 1;
    if (vb === undefined) return -1;
    const diff =
      typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), undefined, { numeric: true });
    return (sort.dir === 'desc' ? -diff : diff) || a.key.localeCompare(b.key);
  });
  const shown = sorted.slice(0, RUN_ROW_CAP);
  const isOpen = (key: string) => (allOpen ? !open.has(key) : open.has(key));

  return (
    <div className={classes.drill}>
      <div className={classes.drillHead}>
        <Typography
          variant="subtitle2"
          component="h4"
          className={classes.drillTitle}
        >
          pr-review runs and their findings
        </Typography>
        <button
          type="button"
          className={classes.expandAll}
          onClick={() => {
            setAllOpen(current => !current);
            setOpen(new Set());
          }}
        >
          {allOpen ? 'Collapse all findings' : 'Expand all findings'}
        </button>
      </div>
      <Table size="small" className={classes.drillTable} aria-label="runs and findings">
        <TableHead>
          <TableRow>
            <SortHead label="PR" k="pr" />
            <SortHead label="Run" k="run" />
            <SortHead label="When" k="when" />
            <SortHead label="Wall-clock" k="wallClock" numeric />
            <SortHead
              label="Cost"
              k="cost"
              numeric
              title="The run's own metering; the per-turn figure rides each cell's tooltip."
            />
            <SortHead label="Turns" k="turns" numeric />
            <SortHead label="Tokens" k="tokens" numeric />
            <TableCell>Models</TableCell>
            <SortHead
              label="Files"
              k="files"
              numeric
              title="Distinct files across the run's findings."
            />
            <SortHead
              label="Findings"
              k="findings"
              className={classes.findingCell}
              title="Tracked findings the run reported (advisory counted apart). Expand the row for the entries."
            />
            <SortHead label="Severity" k="severity" title="Sorts by the run's worst severity." />
            <SortHead label="Status" k="status" title="Sorts by open findings, most first." />
            <SortHead label="Rounds" k="rounds" numeric title="The run's latest known round." />
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map(g => {
            const { run } = g;
            const slug = run
              ? { org: run.org, repo: run.repo }
              : (g.orphanSlug as { org: string; repo: string });
            const runId = run?.runId ?? (g.orphanRunId as string);
            const pr = run?.pr ?? g.findings[0]?.pr;
            const expanded = isOpen(g.key);
            const trackedCount = tracked(g).length;
            const advisoryCount = g.findings.length - trackedCount;
            return (
              <React.Fragment key={g.key}>
                <TableRow>
                  <TableCell className={classes.numeric}>
                    {g.findings.length > 0 ? (
                      <span className={classes.expandSlot}>
                        <button
                          type="button"
                          className={classes.expandBtn}
                          onClick={() => toggleOpen(g.key)}
                          aria-expanded={expanded}
                          aria-label={`${expanded ? 'Hide' : 'Show'} the ${
                            g.findings.length
                          } finding entr${
                            g.findings.length === 1 ? 'y' : 'ies'
                          } of run ${runId}`}
                        >
                          {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                        </button>
                      </span>
                    ) : (
                      <span className={classes.expandSlot} aria-hidden="true" />
                    )}
                    {pr !== undefined ? (
                      <Link
                        href={githubUrl(slug.org, slug.repo, 'pull', pr)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {slug.repo}#{pr}
                      </Link>
                    ) : (
                      <span className={classes.quietText}>{slug.repo}</span>
                    )}
                  </TableCell>
                  <TableCell className={classes.numeric}>
                    <Link
                      href={githubUrl(slug.org, slug.repo, 'actions', 'runs', runId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open run ${runId} on GitHub`}
                    >
                      {runId}
                    </Link>
                  </TableCell>
                  <TableCell className={classes.numeric}>
                    {run
                      ? formatReceivedAt(run.receivedAt, tz) ??
                        dash(
                          classes,
                          'This run carries an unreadable timestamp — reported, but when is unknown.',
                        )
                      : dash(
                          classes,
                          'This run is outside the fetched union — older than raw retention, or outside the displayed period.',
                        )}
                  </TableCell>
                  <TableCell align="right" className={classes.numeric}>
                    {run ? (
                      run.durationMs !== undefined ? (
                        formatDurationMs(run.durationMs)
                      ) : (
                        dash(
                          classes,
                          "Not reported: this run's emitter predates the wall-clock field — absent, never zero.",
                        )
                      )
                    ) : (
                      dash(classes, 'Run outside the fetched union.')
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.numeric}>
                    {run ? (
                      run.costUsd !== undefined ? (
                        (() => {
                          const turns = run.models.reduce((n, m) => n + m.msgs, 0);
                          return (
                            <Tooltip
                              title={
                                turns > 0
                                  ? `${formatUsd(
                                      (run.costUsd as number) / turns,
                                    )} per turn (${turns} turns) — the run's own metering.`
                                  : "The run's own metering."
                              }
                            >
                              <span tabIndex={0}>{formatUsd(run.costUsd)}</span>
                            </Tooltip>
                          );
                        })()
                      ) : (
                        dash(
                          classes,
                          'No cost reported for this run (it predates the cost field) — not free.',
                        )
                      )
                    ) : (
                      dash(classes, 'Run outside the fetched union.')
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.numeric}>
                    {run ? (
                      run.models.reduce((n, m) => n + m.msgs, 0)
                    ) : (
                      dash(classes, 'Run outside the fetched union.')
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.numeric}>
                    {run ? (
                      formatTokens(
                        run.models.reduce(
                          (n, m) =>
                            n +
                            m.inputTokens +
                            m.outputTokens +
                            m.cacheWriteTokens +
                            m.cacheReadTokens,
                          0,
                        ),
                      )
                    ) : (
                      dash(classes, 'Run outside the fetched union.')
                    )}
                  </TableCell>
                  <TableCell>
                    {run ? (
                      run.models.map(m => (
                        <Typography
                          key={m.model}
                          variant="caption"
                          component="div"
                          className={classes.numeric}
                        >
                          {m.model} · {m.msgs} msgs ·{' '}
                          {formatTokens(
                            m.inputTokens +
                              m.outputTokens +
                              m.cacheWriteTokens +
                              m.cacheReadTokens,
                          )}{' '}
                          tok
                        </Typography>
                      ))
                    ) : (
                      dash(classes, 'Run outside the fetched union.')
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.numeric}>
                    {g.findings.length ? (
                      distinctFiles(g)
                    ) : (
                      <span className={classes.quietText}>—</span>
                    )}
                  </TableCell>
                  <TableCell className={classes.findingCell}>
                    {g.findings.length ? (
                      `${trackedCount} finding${trackedCount === 1 ? '' : 's'}${
                        advisoryCount ? ` (+${advisoryCount} advisory)` : ''
                      }`
                    ) : (
                      dash(
                        classes,
                        'No tracked finding is attributed to this run — a clean round, or its findings were last written by a later run.',
                      )
                    )}
                  </TableCell>
                  <TableCell className={classes.numeric}>
                    {trackedCount ? (
                      severityMix(g)
                    ) : (
                      <span className={classes.quietText}>—</span>
                    )}
                  </TableCell>
                  <TableCell className={classes.numeric}>
                    {trackedCount ? (
                      statusMix(g)
                    ) : (
                      <span className={classes.quietText}>—</span>
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.numeric}>
                    {maxRound(g) ?? <span className={classes.quietText}>—</span>}
                  </TableCell>
                </TableRow>
                {expanded && g.findings.length > 0 && (
                  <TableRow key={`${g.key}:labels`}>
                    {/* The entries' column guide — same width classes as the
                        entries themselves, so the labels ARE the alignment.
                        Gated with the entries, not on `expanded` alone:
                        "Expand all" marks EVERY run open, including
                        finding-less ones, and a label strip over nothing
                        would be an orphan. */}
                    <TableCell colSpan={13} className={classes.subCell}>
                      <div className={`${classes.findingSubrow} ${classes.subLabels}`}>
                        <span className={classes.subSeverity}>Severity</span>
                        <span className={classes.subStatus}>Status</span>
                        <span className={classes.subFile}>File</span>
                        <span className={classes.subTitle}>Finding · lens</span>
                        <span className={classes.subRounds}>Rounds</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {expanded &&
                  g.findings.map(f => (
                    <TableRow key={`${g.key}:${f.org}/${f.repo}#${f.pr}:${f.findingId}`}>
                      {/* One full-width line per entry: no empty run-grid cells,
                          readable at ANY viewport width. */}
                      <TableCell colSpan={13} className={classes.subCell}>
                        <div className={classes.findingSubrow}>
                          <span className={classes.subSeverity}>
                            {SEVERITY_LABELS[f.severity ?? 'unclassified']}
                          </span>
                          <span className={classes.subStatus}>
                            <Tooltip title={f.statusDetail}>
                              <span tabIndex={0}>{STATE_LABELS[f.state]}</span>
                            </Tooltip>
                          </span>
                          <span className={classes.subFile}>
                            {f.file ? (
                              <Tooltip
                                title={`${f.file}${
                                  f.line !== undefined ? `:${f.line}` : ''
                                }`}
                              >
                                <span tabIndex={0}>
                                  {f.file.split('/').pop()}
                                  {f.line !== undefined ? `:${f.line}` : ''}
                                </span>
                              </Tooltip>
                            ) : (
                              dash(classes, 'This finding carries no file location.')
                            )}
                          </span>
                          <span className={classes.subTitle}>
                            {f.findingId} · {f.classText}
                            {f.advisory ? ' (advisory)' : ''}{' '}
                            <span className={classes.quietText}>
                              · {PASS_LABELS[f.pass]}
                            </span>
                          </span>
                          <span className={classes.subRounds}>
                            {f.firstRound !== undefined ? String(f.firstRound) : '—'}
                            {f.statusRound !== undefined &&
                            f.statusRound !== f.firstRound
                              ? ` → ${f.statusRound}`
                              : ''}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      <Typography
        variant="caption"
        color="textSecondary"
        component="div"
        className={classes.drillNote}
      >
        {sorted.length > RUN_ROW_CAP
          ? `Showing ${RUN_ROW_CAP} of ${sorted.length} runs in the chosen order. `
          : ''}
        {data.truncated
          ? 'The run union exceeded the serving cap — the oldest slice is shown; narrow the group or period for the rest. '
          : ''}
        {data.droppedRuns > 0
          ? `${data.droppedRuns} run row(s) failed validation and are excluded. `
          : ''}
        One main row per run; expand it for the finding entries (severity · status ·
        file · title · rounds, severity first then file). Findings are attributed to
        the run that last reported them; “Unclassified” severity means none was
        recorded, not that it is low.
      </Typography>
    </div>
  );
};

/**
 * The headline quality figures for the CURRENT scope (period + narrowing),
 * shown above the Breakdown table so spend and outcome share one frame.
 * Renders nothing when no findings payload exists; a withheld scope renders
 * its reason instead of figures.
 */
export const ReviewQualitySummaryStrip = ({
  findings,
  withheldReason,
  droppedRows,
  rowFilterActive = false,
}: {
  findings: readonly ReviewFindingRow[];
  withheldReason?: string;
  droppedRows: number;
  /** The Breakdown's free-text row filter does NOT scope these figures (the
   *  narrowing chips do); when it is active the strip says so and steps back,
   *  so nobody quotes a period figure as a filtered one. */
  rowFilterActive?: boolean;
}) => {
  const classes = useStyles();
  if (withheldReason) {
    return (
      <Typography variant="caption" color="textSecondary" component="div">
        Review quality withheld for this view: {withheldReason}
      </Typography>
    );
  }
  const s = summarizeReviewQuality([...findings]);
  if (s.total === 0 && s.advisory === 0 && droppedRows === 0) return null;
  const figures: Array<{ label: string; value: string; hint: string }> = [
    {
      label: 'fix rate',
      value: formatFixRate(s.fixRate),
      hint: `${s.fixed} of ${s.total} tracked findings fixed in this view`,
    },
    {
      label: 'median rounds to fix',
      value: s.medianRoundsToFix === undefined ? '—' : String(s.medianRoundsToFix),
      hint: 'from first seen to fix evidence',
    },
    {
      label: 'declined',
      value: String(s.declined),
      hint: 'rejected by an engineer — the noise signal',
    },
    {
      label: 'open at merge',
      value: s.withMergeState === 0 ? '—' : String(s.openAtMerge),
      hint:
        s.withMergeState === 0
          ? 'no merge states reported in this view'
          : 'merged with the finding still open',
    },
  ];
  return (
    <div>
      {/* Only the figures step back under the row filter — the scope note and
          any dropped-rows error must keep full contrast. */}
      <div
        style={rowFilterActive ? { opacity: 0.6 } : undefined}
        className={classes.summary}
        aria-label={
          rowFilterActive
            ? 'review quality for the period and narrowing (the row filter is not applied)'
            : 'review quality for this view'
        }
      >
        {figures.map(f => (
          <Tooltip key={f.label} title={f.hint}>
            <Typography variant="body2" component="span" tabIndex={0}>
              <span className={classes.summaryFigure}>{f.value}</span>{' '}
              <span className={classes.summaryLabel}>{f.label}</span>
            </Typography>
          </Tooltip>
        ))}
      </div>
      {rowFilterActive && (
        <Typography variant="caption" color="textSecondary" component="div">
          Scoped to the period and narrowing chips — the row filter below is not
          applied to these figures.
        </Typography>
      )}
      {droppedRows > 0 && (
        <Typography variant="caption" color="error" component="div">
          {droppedRows} finding row(s) failed validation and are excluded; review-quality
          figures cover the rest.
        </Typography>
      )}
    </div>
  );
};
